import { UserData } from 'src/State/User/UserModel'
import React, { useCallback, useMemo } from 'react'
import CompanySettingsUserDataModel from '../models/company-settings-user-data.model'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { getDisplayName } from 'src/Libs/user'
import CompanyAccessPermissionsField from 'src/Libs/company-settings/components/CompanyAccessPermissionsField'
import { CompanyInstallerTableItemModel, CompanyInvitationModel } from 'src/Libs/company-settings/models'
import { Box, Stack } from '@mui/material'
import { ReSendInviteButton } from 'src/Libs/company-settings/components/buttons'

interface HeaderData {
  label: string
  key: string
}

interface UseCompanyUserTableHook {
  /**
   *
   *
   * @param companyId
   */
  data: React.ReactNode[][]
  header: HeaderData[]
}

const DEFAULT_NO_DATA = '-'

/**
 * Provides pre-build table items and header information for the provided user composition models
 */
const useCompanyUserTable = (
  users: CompanyInstallerTableItemModel[],
  companyId: number,
  hasCompanyShopRights: boolean,
  onChangeShop: (value: boolean, userId: number) => void,
  onChangeNotifications: (value: boolean, companyId: number, userId: number, notificationId: number | null) => void,
  onReSendInvite: (companyId: number, token: string) => void
): UseCompanyUserTableHook => {
  const { t } = useCustomTranslation()

  const doDisplayPermissionColumn = hasCompanyShopRights

  const buildDisplayNameCell = (item: CompanyInstallerTableItemModel): string => {
    if (!item) {
      return DEFAULT_NO_DATA
    }

    if (item?.isInvite) {
      return item.displayName
    }

    return getDisplayName(item.value as UserData)
  }
  const buildRoleDisplayNameCell = (item: CompanyInstallerTableItemModel): string => {
    return item.role
  }

  const buildStateCell = useCallback(
    (item: CompanyInstallerTableItemModel): React.ReactNode => {
      if (item.isInvite) {
        const model = item.value as CompanyInvitationModel
        return (
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
            <Box>{t('user.company.invitation.invited')}</Box>
            <ReSendInviteButton
              onClick={onReSendInvite}
              companyId={companyId}
              token={model.token}
              confirmText={t('user.company.reInvitation.dialog.text')}
              confirmTitle={t('user.company.reInvitation.dialog.title')}
            >
              {t('user.company.reInvitation.controlLabel')}
            </ReSendInviteButton>
          </Stack>
        )
      }
      return t('user.company.invitation.unlocked')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onReSendInvite, companyId]
  )

  const buildPermissionCell = (item: CompanyInstallerTableItemModel): React.ReactNode => {
    if (item?.isInvite) {
      return <span />
    }
    const model = item?.value as CompanySettingsUserDataModel
    const hasNotificationAccess = Boolean(model.hasErrorNotification)
    const hasShopAccess = Boolean(model.permissions.useShop)
    return (
      <CompanyAccessPermissionsField
        hasShop={Boolean(doDisplayPermissionColumn)}
        hasNotificationAccess={hasNotificationAccess}
        onToggleNotificationAccess={onChangeNotifications}
        hasShopAccess={hasShopAccess}
        onToggleShopAccess={onChangeShop}
        userId={model.ID}
        notificationId={model.errorNotificationId}
        companyId={companyId}
      />
    )
  }

  const headerKeys = ['user.company.table.userDisplayName', 'user.company.table.userLevel', 'user.company.table.inviteStatus']

  if (doDisplayPermissionColumn) headerKeys.push('user.company.table.permissions')

  const header = headerKeys.map(elem => ({
    label: t(elem),
    key: elem,
  }))

  const data = useMemo<React.ReactNode[][]>(() => {
    return users.map(userItem => {
      const tableCells = []
      tableCells.push(buildDisplayNameCell(userItem), buildRoleDisplayNameCell(userItem), buildStateCell(userItem))
      if (doDisplayPermissionColumn) tableCells.push(buildPermissionCell(userItem))
      return tableCells
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users])

  return { header, data }
}

export default useCompanyUserTable
